import { createCanvasElement } from "./canvas";

export const loadImage = (src: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();

    image.src = src;
    image.addEventListener("load", () => {
      resolve(image);
    });
    image.addEventListener("error", () => {
      reject(image);
    });
  });

type FileReadResult = {
  name: string;
  dataUrl: string;
};

export const readAsDataURL = (file: File): Promise<FileReadResult> =>
  new Promise((resolve, reject) => {
    const fileReader = new FileReader();

    fileReader.addEventListener("load", () =>
      resolve({
        name: file.name,
        dataUrl: fileReader.result as string,
      }),
    );
    fileReader.addEventListener("error", reject);
    fileReader.readAsDataURL(file);
  });

export const getImageElementSize = ({ naturalWidth: width, naturalHeight: height }: HTMLImageElement) => ({
  width,
  height,
});

export const getImageSize = (src: string): Promise<{ height: number; width: number }> =>
  loadImage(src).then(getImageElementSize);

export const resizeImageElement = (image: HTMLImageElement, scaleFactor = 1): string => {
  const { height, width } = getImageElementSize(image);
  const canvas = document.createElement("canvas");

  const imgWidth = width * scaleFactor;
  const imgHeight = height * scaleFactor;

  const ctx = canvas.getContext("2d");

  canvas.width = imgWidth;
  canvas.height = imgHeight;
  ctx.drawImage(image, 0, 0, imgWidth, imgHeight);

  return canvas.toDataURL();
};

export const normalizeImage = (src: string, maxSize: number) => {
  return src.startsWith("data:image/svg+xml")
    ? src
    : loadImage(src).then(image => {
        const { height, width } = getImageElementSize(image);
        const size = Math.max(height, width);

        if (size > maxSize) {
          return resizeImageElement(image, maxSize / size);
        }

        return src;
      });
};

export const getImageDataUri = (src: string): Promise<string> =>
  loadImage(src).then(image => {
    if (src.startsWith("data:image/svg+xml")) {
      return src;
    }

    const canvas = document.createElement("canvas");

    canvas.width = image.naturalWidth;
    canvas.height = image.naturalHeight;

    const context = canvas.getContext("2d");

    context.drawImage(image, 0, 0);

    return canvas.toDataURL();
  });

export const resizeImageSize = (image: Pick<HTMLImageElement, "width" | "height">, size: number) => {
  const { width, height } = image;
  const ratio = size / Math.max(width, height);

  return {
    width: width * ratio,
    height: height * ratio,
  };
};

export const getByteStringFromDataUri = (dataUri: string) => dataUri && dataUri.split(",")[1];

export const dataUriToBlob = (dataUri: string): Blob => {
  const byteString = atob(getByteStringFromDataUri(dataUri));
  const mimeString = dataUri.split(",")[0].split(":")[1].split(";")[0];
  const ab = new ArrayBuffer(byteString.length);
  const ia = new Uint8Array(ab);

  for (let i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i);
  }

  return new Blob([ab], { type: mimeString });
};

export const dataUriToFile = (dataUri: string, fileName: string): File => {
  const blob = dataUriToBlob(dataUri);

  return new File([blob], fileName, blob);
};

export const getUrlFromBlob = (blob: Blob) => URL.createObjectURL(blob);

export const downloadFileBlob = (blob: Blob, name: string, ext?: string) => {
  const url = getUrlFromBlob(blob);
  const a = document.createElement("a");

  a.style.display = "none";
  a.href = url;
  a.download = `${name}${ext ? `.${ext}` : ""}`;
  document.body.appendChild(a);
  a.click();

  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
};

export const svgToPng = async (svgString: string, size: number): Promise<string> => {
  return new Promise(resolve => {
    const canvas = createCanvasElement(size, size);
    const context = canvas.getContext("2d");

    const img = new Image();

    img.onload = function () {
      context.drawImage(img, 0, 0);
      resolve(canvas.toDataURL("image/png"));
    };

    img.src = svgString;
  });
};

export const getFileBySrc = async (src: string, fileName: string): Promise<File> => {
  return fetch(src)
    .then((response: Response) => response.blob())
    .then((blob: Blob) => new File([blob], fileName));
};

export const getDataFromDataURI = (dataURI: string) => dataURI.split(",")[1];

/**
 * Loads SVG from URL (webpack import) and converts to base64 data URI
 * Used for print legend compound icons
 */
export const loadSvgAsDataUri = async (svgUrl: string): Promise<string> => {
  // If already a data URI, return as is
  if (svgUrl.startsWith("data:")) {
    return svgUrl;
  }

  // Fetch SVG content
  const response = await fetch(svgUrl);
  const svgText = await response.text();

  // Convert to base64 data URI
  const base64 = btoa(unescape(encodeURIComponent(svgText)));

  return `data:image/svg+xml;base64,${base64}`;
};
